import { Button, Input, Select, Space } from "antd";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useGetCoursesCategoryQuery } from "../../../app/features/courses/courses.api";
import DropdownIcon from "../../../assets/icons/DropdownIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import useError from "../../../hooks/useError";
import { useQueryFilters } from "../../../hooks/useQueryFilters";

export default function Filters() {
  const { filters, setFilterValue, setFilterValues } = useQueryFilters();
  const [searchQuery, setSearchQuery] = useState(
    (filters.search as string) || ""
  );

  const { data: categories, isLoading, error } = useGetCoursesCategoryQuery();
  useError(error, "ক্যাটাগরি লোড করতে ব্যর্থ হয়েছে");

  // Sync local state when URL changes (e.g. back button)
  useEffect(() => {
    setSearchQuery((filters.search as string) || "");
  }, [filters.search]);

  // Debounce the search URL update
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilterValue("search", value);
      }, 500),
    [setFilterValue]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSearchSubmit = () => {
    setFilterValue("search", searchQuery);
  };

  return (
    <div className="relative -mb-36 md:-mb-28 mt-10 ">
      <Space
        size="middle"
        align="center"
        wrap
        className="w-full p-4 bg-white rounded-xl flex flex-wrap gap-3 z-10 shadow-lg
            [&_.ant-space-item:first-child]:flex-1 
            [&_.ant-space-item:first-child]:w-full"
      >
        {/* SEARCH INPUT */}
        <Input
          size="large"
          prefix={<SearchIcon className="text-theme-slate-600" />}
          placeholder="সার্চ করুন"
          className="!w-full lg:flex-1 min-w-56"
          value={searchQuery}
          onChange={handleSearchChange}
          onPressEnter={handleSearchSubmit}
        />

        {/* CATEGORY */}
        <Select
          allowClear
          size="large"
          suffixIcon={<DropdownIcon className="text-theme-slate-600" />}
          placeholder="ক্যাটাগরি"
          className="w-full lg:w-40"
          loading={isLoading}
          options={categories?.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          popupMatchSelectWidth={200}
          value={filters.categoryId ? Number(filters.categoryId) : null}
          onChange={(value) =>
            setFilterValues({ categoryId: value, search: filters.search })
          }
        />

        {/* TIME */}
        <Select
          allowClear
          size="large"
          suffixIcon={<DropdownIcon className="text-theme-slate-600" />}
          placeholder="সময়কাল"
          className="w-full lg:w-40"
          options={durations}
          value={filters.duration ? Number(filters.duration) : null}
          onChange={(value) =>
            setFilterValues({ duration: value, search: filters.search })
          }
        />

        {/* COURSE TYPE */}
        <Select
          allowClear
          size="large"
          suffixIcon={<DropdownIcon className="text-theme-slate-600" />}
          placeholder="কোর্সের ধরণ"
          className="w-full lg:w-40"
          options={levels}
          value={filters.level}
          onChange={(value) =>
            setFilterValues({ level: value, search: filters.search })
          }
        />

        {/* BUTTON */}
        <Button
          type="primary"
          size="large"
          className="w-full lg:w-40 pt-2"
          onClick={handleSearchSubmit}
        >
          সার্চ করুন
        </Button>
      </Space>
    </div>
  );
}

const levels = [
  {
    label: "প্রাথমিক",
    value: "BEGINNER",
  },
  {
    label: "মাধ্যমিক",
    value: "INTERMEDIATE",
  },
  {
    label: "অ্যাডভান্সড",
    value: "ADVANCED",
  },
];

const durations = [
  { label: "৩০ - ৪০ মিনিট", value: 1 },
  { label: "৪০ - ৫০ মিনিট", value: 2 },
  { label: "৫০ - ৬০ মিনিট", value: 3 },
];

// eslint-disable-next-line react-refresh/only-export-components
export const durationValues = {
  1: [30, 40],
  2: [40, 50],
  3: [50, 60],
};
